import {
  App,
  AppMeteredEventCountry,
  AppSubscription,
  AppSubscriptionCountry,
  LocalizedApp,
  SuggestedApp,
} from '../models/app';
import { reconcileI18LanguageWithServerResponseLanguage, convertToSentenceCase } from '../utils/transformationHelper';
import defaultImg from '../assets/icon_not_loading.png';
import { formatMeteredAmount, formatSubscriptionAmount, FormattedCurrency } from '../utils/currency';
import i18n, { TFunction } from 'i18next';
import { Elements } from '../models/common';
import { getAppMarketMode } from '../utils/session';
import { APP_FREE_TRIAL_LENGTH, WIRELESS_MANAGER_APP, YELP_APP_ID } from '../utils/constants';
import { AppRelatedTranslationKeys } from '../pages/AppDetails/types';
import { AppMode } from '../models/enums';

export interface SubscriptionObj {
  activeMeteredBilling: AppMeteredEventCountry[];
  availableSubscriptionsArr: AppSubscriptionCountry[];
  bestSubscriptionOption: Maybe<AppSubscriptionCountry>; // cheapest
  currentlySelectedSubscription: Maybe<AppSubscriptionCountry>;
  formattedSubscriptionArr: FormattedCurrency[];
  freeVersionText: FreeVersionText;
  subscriptionRelatedTextOption: SubscriptionPricingOption;
  trialText: FreeTrialText;
}

type FreeVersionText = Maybe<{
  freePlan: string,
  normal: string,
}>

export type FreeTrialText = Maybe<{
  long: string;
  short: string;
  endsInDays?: string
}>

type SubscriptionPricingOption = {
  text: string,
  isALink?: boolean
}

export type AppFeeColumn = {
  topText: string;
  bottomText?: string;
}

const DAY_MILLIS = 1000 * 60 * 60 * 24;

export const getLocalizedAppDetails = (app: App, rawLanguage?: string): Partial<LocalizedApp> => {
  const language = reconcileI18LanguageWithServerResponseLanguage(rawLanguage);
  const matchedLocale = getMatchedLocale(app.locales, language);
  return {
    name: matchedLocale.name || app.name || '',
    description: matchedLocale.description || app.description,
    tagline: matchedLocale.tagline || app.tagline,
    filenameIconSmall: matchedLocale.filenameIconSmall || app.filenameIconSmall || defaultImg,
    filenameBanner: matchedLocale.filenameBanner || app.filenameBanner || '',
    filenameCover: matchedLocale.filenameCover || app.filenameCover || '',
    videoUrl: matchedLocale.videoUrl || app.videoUrl,
    screenshots: matchedLocale.screenshots || app.screenshots,
  };
};

export const getLocalizedSuggestedApp = (suggestedApp: SuggestedApp, rawLanguage?: string): Partial<LocalizedApp> => {
  const language = reconcileI18LanguageWithServerResponseLanguage(rawLanguage).toLowerCase();
  const matchedLocale = getMatchedLocale(suggestedApp.app.locales, language);
  return {
    name: matchedLocale.name || suggestedApp.app.name || '',
    filenameIconSmall: matchedLocale.filenameIconSmall || suggestedApp.app.filenameIconSmall || defaultImg,
  };
};

export const getSubscriptionCountry = (subscriptionElement: Maybe<AppSubscription>): Maybe<AppSubscriptionCountry> => {
  if (!subscriptionElement) return;
  for (let element of subscriptionElement.subscriptionCountries?.elements || []) {
      return element;
  }
};

/**
 *
 * @param app App
 * The server filters out subscription based on the merchant's region.
 * For loggedOut mode the Server filters out the subscription based on the clientCountry in the endpoint.
 */
export const getSubscription = (app: Maybe<App>): SubscriptionObj => {
  let freeVersionAvailable = false,
    activeMeteredBilling: AppMeteredEventCountry[] = [],
    availableSubscriptionsArr: AppSubscriptionCountry[] = [],
    bestSubscriptionOption: Maybe<AppSubscriptionCountry>,
    currentlySelectedSubscription: Maybe<AppSubscriptionCountry>,
    formattedSubscriptionArr: FormattedCurrency[] = [],
    freeVersionText: FreeVersionText,
    subscriptionRelatedTextOption: SubscriptionPricingOption,
    trialText = getTrialMessage(app);

  if (app) {
    const { availableSubscriptions, availableMetereds, currentSubscription } = app;
    let cheapest;
    for (let subscriptionElement of availableSubscriptions?.elements || []) {
      let availableSubscription = getSubscriptionCountry(subscriptionElement);
      if (availableSubscription) {
        if (!cheapest || availableSubscription.amount < cheapest.amount) cheapest = availableSubscription;
        availableSubscriptionsArr.push(availableSubscription);
        if (!freeVersionAvailable && availableSubscription.amount === 0) {
          freeVersionAvailable = true;
        }
        formattedSubscriptionArr.push(formatSubscriptionAmount(availableSubscription.amount, availableSubscription.country, true));
      }
    }

    bestSubscriptionOption = cheapest;
    currentlySelectedSubscription = getSubscriptionCountry(currentSubscription);
    if (availableMetereds) activeMeteredBilling = getActiveMeteredBilling(app);
    freeVersionText = getFreeVersionText(app, freeVersionAvailable);
  }

  if (availableSubscriptionsArr.length) {
    availableSubscriptionsArr.sort((a, b) => a.amount - b.amount);
  }
  subscriptionRelatedTextOption = getSubscriptionRelatedOption(availableSubscriptionsArr, currentlySelectedSubscription);

  return {
    activeMeteredBilling,
    availableSubscriptionsArr,
    bestSubscriptionOption,
    currentlySelectedSubscription,
    formattedSubscriptionArr,
    freeVersionText,
    subscriptionRelatedTextOption,
    trialText,
  };
};

export const getGenericTrialMessage = (trialDays?: number): string => {
  let resultStr = i18n.t('app_details.trial_available');
  if (trialDays) {
    resultStr += ` ${i18n.t('app_details.for_days', { days: trialDays })}`;
  }
  return convertToSentenceCase(resultStr);
};

export const appHasTrialOption = (app: Maybe<App>): boolean => {
  return !!app && !!app.paidAppHasTrial;
};

export const appHasNonzeroSubscriptions = (availableSubscriptions: Maybe<Elements<AppSubscription>>): boolean => {
  return !isAppFree(availableSubscriptions);
};

export const availableSubscriptionsLength = (app: App): number => {
  let result = 0;
  const { availableSubscriptions } = app;
  for (let subscriptionElement of availableSubscriptions?.elements || []) {
    let availableSubscription = getSubscriptionCountry(subscriptionElement);
    if (availableSubscription) ++result;
  }
  return result;
};

export const isAppFree = (availableSubscriptions: Maybe<Elements<AppSubscription>>): boolean => {
  let elements = availableSubscriptions?.elements || [],
    numAvailableSubscriptions = elements.length,
    amountOfFirstSubscription = elements[0]?.subscriptionCountries?.elements[0].amount;

  return numAvailableSubscriptions === 1 && !!amountOfFirstSubscription && amountOfFirstSubscription === 0;
};

export const appHasAvailableMetered = (app: Maybe<App>): boolean => {
  if (!app?.availableMetereds?.elements.length) return false;

  let { elements: meteredElements } = app.availableMetereds;
  for(let meteredElement of meteredElements) {
    let meteredCountryEvent = meteredElement.meteredCountries.elements[0];
    if (meteredCountryEvent.active) return true;
  }
  return false;
};

const appHasFreeVersionAvailable = (app: App): boolean => {
  const { availableSubscriptions } = app;
  for (let subscriptionElement of availableSubscriptions?.elements || []) {
    if (getSubscriptionCountry(subscriptionElement)?.amount === 0) return true;
  }
  return false;
};

export const getTrialMessage = (app: Maybe<App>): FreeTrialText => {
  const trialDaysLeft = app?.trialDaysLeft;
  if (!appHasTrialOption(app) || (isAppFree(app?.availableSubscriptions) && !appHasAvailableMetered(app)) || !trialDaysLeft) return;

  const { trialDays = 0, currentSubscription, installed } = app!;

  // Reaching here means: app has trial days left and paidAppHasTrial is true and (app has atleast one non zero subscription OR app has available metered)
  let result;
  if (installed) {
    const subscription = currentSubscription?.subscriptionCountries?.elements[0];
    if (subscription?.amount === 0 || currentSubscription?.plan) {
      // don't show trial message if they're on a free tier or free as part of a plan
      return;
    } else {
      result = {
        long: i18n.t('app_details.trial_message_long_installed', { days: trialDaysLeft }),
        short: i18n.t('app_details.trial_message_short_installed', { days: trialDaysLeft }),
        endsInDays: i18n.t('app_details.trial_message_ends_in_days', { days: trialDaysLeft }),
      };
    }
  } else if (getAppMarketMode() === AppMode.Public || trialDaysLeft >= (trialDays || APP_FREE_TRIAL_LENGTH)) {
    // if not logged in as a merchant, or trial isn't started, show default message
    result = {
      long: i18n.t('app_details.trial_message_long', { days: trialDays || APP_FREE_TRIAL_LENGTH }),
      short: i18n.t('app_details.trial_message_short'),
    };
  } else {
    // it's not installed, but they had it installed previously, some trial days are still left
    result = {
      long: i18n.t('app_details.trial_message_long_uninstalled_still_used', { days: trialDaysLeft }),
      //short: i18n.t('app_details.trial_message_short_uninstalled_still_used', { days: trialDaysLeft }),
      short: i18n.t('app_details.trial_message_short'),
      endsInDays: i18n.t('app_details.trial_message_ends_in_days', { days: trialDaysLeft }),
    };
  }
  return result;
};

const getFreeVersionText = (app: App, freeVersionAvailable?: boolean): FreeVersionText => {
  let result;
  freeVersionAvailable = freeVersionAvailable !== undefined ? freeVersionAvailable : appHasFreeVersionAvailable(app);
  if (!app.installed && freeVersionAvailable) {
    result = {
      normal: `${i18n.t('app_details.free_version_available')}`,
      freePlan: `${i18n.t('app_details.free_plan_available')}`,
    };
  }
  return result;
};

export const getSubscriptionRelatedOption = (availableSubscriptionsArr: Maybe<AppSubscriptionCountry[]>, currentlySelectedSubscription: Maybe<AppSubscriptionCountry>): SubscriptionPricingOption => {
  let result: SubscriptionPricingOption = {
    text: '',
  };
  if (availableSubscriptionsArr && availableSubscriptionsArr.length) {
    if (currentlySelectedSubscription) {
      result = {
        text: currentlySelectedSubscription.amount ? i18n.t(AppRelatedTranslationKeys.SubscriptionPricing) : '', //donot sow subscription pricing text if amount is zero
      };
    } else {
      result = {
        text: i18n.t(AppRelatedTranslationKeys.ViewSubscription),
        isALink: true,
      };
    }
  }
  return result;
};

export const getAppFeeCol = (app: App, subscription: Maybe<AppSubscriptionCountry>, t: TFunction): AppFeeColumn => {
  let feeCol: AppFeeColumn = {
    topText: '',
    bottomText: '',
  };

  if (!subscription) return feeCol;

  const formattedSubscription = formatSubscriptionAmount(subscription.amount, subscription.country),
    trialMessage = getTrialMessage(app),
    activeMetered = getActiveMeteredBilling(app),
    activeMeteredBillingString = getMeteredBillingString(activeMetered);

  if (trialMessage?.endsInDays) {
    feeCol.topText = t('app_details.free_trial');
    feeCol.bottomText = `${trialMessage.endsInDays} ${t('amount_after', { amount: formattedSubscription?.short || '' })}`;
  } else if (formattedSubscription) {
    let payPerActionString = activeMeteredBillingString ? `${t('general.pay')} ${activeMeteredBillingString}` : '';
    feeCol.topText = formattedSubscription.amount === 0 && payPerActionString ? payPerActionString : formattedSubscription.short;
    feeCol.bottomText = formattedSubscription.amount !== 0 && payPerActionString ? payPerActionString : '';
  }

  return feeCol;
};

export const getActiveMeteredBilling = (app: Maybe<App>): AppMeteredEventCountry[] => {
  if (!app?.availableMetereds) return [];
  const {availableMetereds} = app;
  return availableMetereds.elements.map((meteredEvent) => meteredEvent.meteredCountries.elements[0]).filter(meteredEvent => meteredEvent.active);
};

export const getMeteredBillingString = (meteredEvents: AppMeteredEventCountry[]): string => {
  const formattedEventAmounts = meteredEvents.map((meteredEvent) => formatMeteredAmount(meteredEvent.amount, meteredEvent.action, meteredEvent.country));

  if (formattedEventAmounts.length > 1) return formattedEventAmounts.join(` ${i18n.t('general.and')} `);
  return formattedEventAmounts[0] || '';
};

export const isHipaaComplaintApp = (app: Maybe<App>): boolean => {
  return !!app && app.isHipaaCompliant !== false;
};

export const needToUninstallDueToHipaa = (app: App): boolean => {
  return !!app.installed && !isHipaaComplaintApp(app);
};

export const isAppAndroid= (app:App):boolean=>{
  return !!app.packageName && !!app.packageName.trim();
}

export const isYelpApp = (app: App): boolean => app.id === YELP_APP_ID;
export const isWirelessManagerApp = (app: App): boolean => app.id === WIRELESS_MANAGER_APP;

export const isWebApp =(app:App):boolean => {
	return !!app.siteUrl && !!app.siteUrl.trim()
}

export const isOnlyAndroidApp = (app:App):boolean=>{
  return !!isAppAndroid(app) && !isWebApp(app)
}

export const subscriptionWithLowestPlan = (subscriptionArr: FormattedCurrency[]) => {
	const subscriptionAmountArr: number[] = [];
	subscriptionArr.filter(subscription => subscription.amount !== 0).forEach((subscription)=>{
		return subscriptionAmountArr.push(subscription.amount);
	})
	const lowestSubscription = Math.min(...subscriptionAmountArr)
	return subscriptionArr.filter(subscription=> subscription.amount === lowestSubscription)
}

const getMatchedLocale = ( locales : Readonly<{elements: readonly Partial<App>[];}> | undefined, language: string): Partial<LocalizedApp> => {
  const { elements } = locales || {};

  if (elements && elements.length) {
    for(let element of elements) {
      if (language === element.locale) {
        return element;
      }
    }
  }
  return {};
}
